/**
 * Plain-text utilities for rendering post body excerpts in card previews.
 *
 * Post bodies are stored as TipTap-produced HTML (e.g. "<p>Well, 13th May
 * 1997…</p><p>I actually watched…</p>"). On post DETAIL pages we render
 * them via DOMPurify + dangerouslySetInnerHTML. On CARD previews
 * (homepage, archive, retro/blast) we just want the plain text, so we
 * have to strip the tags first — otherwise readers see literal `<p>`
 * markup, which has happened in production (see screenshot, 13 May 2026).
 */

const ENTITY_MAP = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
  "&nbsp;": " ",
  "&hellip;": "…",
  "&mdash;": "—",
  "&ndash;": "–",
  "&rsquo;": "’",
  "&lsquo;": "‘",
  "&rdquo;": "”",
  "&ldquo;": "“",
};

/**
 * Convert HTML to plain text suitable for a card excerpt.
 *
 *   - Strips every tag (no fancy DOM parsing — we don't need it; the
 *     content is already DOMPurify-sanitised on input, so the body never
 *     contains <script>/<style> blocks).
 *   - Collapses runs of whitespace to a single space.
 *   - Decodes the most common HTML entities.
 *   - Treats `<br>`, `</p>`, `</div>`, `</li>` as paragraph breaks so
 *     consecutive paragraphs read as "first paragraph. second paragraph"
 *     in the excerpt rather than running together as one word.
 *
 * Returns "" for falsy input.
 */
export const htmlToPlainText = (html) => {
  if (!html) return "";
  let text = String(html);
  // Block-level closers become a single space so words don't fuse together.
  text = text.replace(/<\s*\/(p|div|li|h[1-6]|blockquote|figure|figcaption|tr|td|th)\s*>/gi, " ");
  text = text.replace(/<\s*br\s*\/?\s*>/gi, " ");
  // Drop all remaining tags.
  text = text.replace(/<[^>]*>/g, "");
  // Decode common entities.
  text = text.replace(/&[a-z#0-9]+;/gi, (m) => (ENTITY_MAP[m] !== undefined ? ENTITY_MAP[m] : m));
  // Collapse whitespace.
  text = text.replace(/\s+/g, " ").trim();
  return text;
};

/**
 * Trim plain text to `maxLen` chars on a word boundary, appending an
 * ellipsis if anything was dropped.
 */
export const truncate = (text, maxLen = 220) => {
  if (!text) return "";
  if (text.length <= maxLen) return text;
  const slice = text.slice(0, maxLen);
  const lastSpace = slice.lastIndexOf(" ");
  const trimmed = lastSpace > maxLen - 40 ? slice.slice(0, lastSpace) : slice;
  return `${trimmed.trimEnd()}…`;
};

/**
 * One-call helper used by card previews: HTML → plain text → truncated.
 */
export const htmlExcerpt = (html, maxLen = 220) => truncate(htmlToPlainText(html), maxLen);

/**
 * Resolve what teaser content to show on a preview card for a given post.
 *
 * If the admin filled in a custom intro on the editor, return it as
 * sanitised HTML so basic inline formatting (bold, italic, links) is
 * preserved on cards. Otherwise return the auto-generated plain-text
 * excerpt from the start of the post body.
 *
 * Returns `{ html, text }` where exactly one of the two is non-empty.
 * Cards should render the HTML branch via dangerouslySetInnerHTML when
 * present, else the plain text branch.
 */
export const postCardIntro = (post, maxLen = 220) => {
  if (!post) return { html: "", text: "" };
  const custom = (post.custom_intro || "").trim();
  if (custom) return { html: custom, text: "" };
  return { html: "", text: htmlExcerpt(post.body, maxLen) };
};

/**
 * Plain-text-only variant of `postCardIntro` for compact list cards (e.g.
 * the archive page) that must never render rich HTML/images — always
 * returns a short, tag-stripped excerpt, preferring `custom_intro`'s text
 * over the body's when a custom intro is set.
 */
export const postCardIntroText = (post, maxLen = 220) => {
  if (!post) return "";
  const source = (post.custom_intro || "").trim() ? post.custom_intro : post.body;
  return htmlExcerpt(source, maxLen);
};
